import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, message, Select } from 'antd';
import { authAxios } from '../utils/session_utils';
import { PlayoutServer } from './types';

const { TextArea } = Input;
const { Option } = Select;

const AWS_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'eu-west-1',
  'eu-central-1',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-northeast-1',
];

interface PlayoutServerFormProps {
  server: PlayoutServer | null;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const PlayoutServerForm: React.FC<PlayoutServerFormProps> = ({ server, open, onClose, onSave }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isLocalInstance, setIsLocalInstance] = useState(false);

  useEffect(() => {
    if (open) {
      if (server) {
        const instanceId = server.ec2_instance_id || '';
        setIsLocalInstance(instanceId === 'local');
        form.setFieldsValue({
          display_name: server.display_name || '',
          name: server.name,
          endpoint_url: server.endpoint_url,
          description: server.description || '',
          ec2_instance_id: instanceId,
          ec2_region: server.ec2_region || 'us-east-1',
        });
      } else {
        setIsLocalInstance(false);
        form.resetFields();
        form.setFieldsValue({
          ec2_region: 'us-east-1',
        });
      }
    }
  }, [open, server, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (server) {
        // Update existing server
        await authAxios({
          method: 'PUT',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}`,
          data: values
        });
        message.success('Playout server updated successfully');
      } else {
        // Create new server
        await authAxios({
          method: 'POST',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers`,
          data: values
        });
        message.success('Playout server created successfully');
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving playout server:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to save playout server');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateEndpointUrl = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Endpoint URL is required'));
    }
    
    try {
      const url = new URL(value);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return Promise.reject(new Error('URL must use http:// or https://'));
      }
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error('Please enter a valid URL'));
    }
  };

  const validateEC2InstanceId = (_: any, value: string) => {
    if (!value) {
      return Promise.resolve(); // Optional field
    }
    
    // Allow "local" for development
    if (value === 'local') {
      return Promise.resolve();
    }
    
    // Validate EC2 instance ID format: i- followed by alphanumeric characters
    const ec2InstanceIdPattern = /^i-[a-z0-9]+$/i;
    if (!ec2InstanceIdPattern.test(value)) {
      return Promise.reject(new Error('EC2 instance ID must start with "i-" followed by alphanumeric characters, or be "local" for development'));
    }
    
    return Promise.resolve();
  };

  return (
    <Modal
      title={server ? 'Edit Playout Server' : 'Add Playout Server'}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      okText={server ? 'Update' : 'Create'}
      cancelText="Cancel"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '20px' }}
      >
        <Form.Item
          name="display_name"
          label="Display Name"
          extra="Optional friendly name shown as the primary label. If blank, server name is used."
          rules={[
            { max: 100, message: 'Display name must be less than 100 characters' }
          ]}
        >
          <Input placeholder="e.g., NYC Production, EU Backup" />
        </Form.Item>

        <Form.Item
          name="name"
          label="Server Name (auto-set from EC2)"
          extra="This is auto-populated from EC2 Name tag on start/stop/refresh"
          rules={[
            { required: true, message: 'Please enter a server name' },
            { min: 3, message: 'Name must be at least 3 characters' }
          ]}
        >
          <Input placeholder="e.g., Production Server 1" />
        </Form.Item>

        <Form.Item
          name="endpoint_url"
          label="Endpoint URL"
          rules={[
            { required: true, message: 'Please enter an endpoint URL' },
            { validator: validateEndpointUrl }
          ]}
          extra="The base URL of the playout server's Caddy front door — no port for the standard v1 deploy (e.g., http://ec2-xx.compute-1.amazonaws.com). Matches terraform output api_url."
        >
          <Input placeholder="http://ec2-xx-xx-xx-xx.compute-1.amazonaws.com" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 500, message: 'Description must be less than 500 characters' }
          ]}
        >
          <TextArea 
            rows={3} 
            placeholder="Optional description about this server..."
          />
        </Form.Item>

        <Form.Item
          name="ec2_instance_id"
          label="EC2 Instance ID"
          rules={[
            { validator: validateEC2InstanceId }
          ]}
          extra="Enter EC2 instance ID (e.g., i-1234567890abcdef0) or 'local' for development"
        >
          <Input 
            placeholder="i-1234567890abcdef0 or local"
            onChange={(e) => {
              const value = e.target.value;
              setIsLocalInstance(value === 'local');
              if (value === 'local') {
                form.setFieldsValue({ ec2_region: undefined });
              } else if (!form.getFieldValue('ec2_region')) {
                form.setFieldsValue({ ec2_region: 'us-east-1' });
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="ec2_region"
          label="EC2 Region"
          extra={isLocalInstance ? "N/A for local instances" : "AWS region where the EC2 instance is located"}
        >
          <Select placeholder="Select region" disabled={isLocalInstance}>
            {AWS_REGIONS.map(region => (
              <Option key={region} value={region}>{region}</Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PlayoutServerForm;

